import { makeObservable } from "@faire/web/common/makeObservable";
import { QueryParams } from "@faire/web/common/query-params/QueryParams";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import { Storage } from "@faire/web/common/Storage";
import { createStoreHook } from "@faire/web/ui/hooks/useStore";
import addDays from "date-fns/addDays";
import History from "history";
import isEmpty from "lodash/isEmpty";
import { action, computed, observable } from "mobx";
import { matchPath } from "react-router-dom";

import { isSafari } from "@faire/retailer/lib/browser";
import { isMobileApp } from "@faire/retailer/lib/isMobileApp";
import { getOsName } from "@faire/retailer/lib/user-agent/osName";
import {
  getIsProduction,
  getSafeEnvName,
} from "@faire/retailer/serialized-data/getEnvName";
import { getFaireBaseUrl } from "@faire/retailer/serialized-data/getFaireBaseUrl";
import { getIsAppInstalled } from "@faire/retailer/serialized-data/getIsAppInstalled";
import { getSettingAppPromotionBannerLinks } from "@faire/retailer/settings/getSettingAppPromotionBannerLinks";
import { UserStore } from "@faire/retailer/stores/domain/UserStore";

export const BANNER_FLAG_TIMESTAMP =
  "retailer_mobile-app-banner-flag-timestamp";
const DISMISS_INTERVAL_DAYS = 1;

export enum OSType {
  IOS = "iOS",
  ANDROID = "Android",
}

/**
 * @deprecated prefer to use `useMobileAppBannerService` or `withStores` instead
 */
export class MobileAppBannerService {
  constructor() {
    makeObservable(this);
    this.showBanner = this.shouldShowBanner;
  }

  static get = singletonGetter(MobileAppBannerService);

  @observable
  showBanner: boolean;

  private get bannerLinkUrl(): string | undefined {
    const isAppInstalled = getIsAppInstalled() ?? false;

    const linkUrl =
      (isAppInstalled && this.getDeepLinkUrl) || this.getStoreLinkUrl();

    return linkUrl;
  }

  @action
  dismissBanner = () => {
    Storage.local.setItem(
      BANNER_FLAG_TIMESTAMP,
      addDays(new Date(), DISMISS_INTERVAL_DAYS).getTime().toString()
    );
    this.showBanner = false;
  };

  @action
  updateBannerAvailibility = () => {
    this.showBanner = this.shouldShowBanner;
  };

  private getStoreLinkUrl = () => {
    const appPromotionBannerLinks = getSettingAppPromotionBannerLinks();

    if (getOsName() === OSType.IOS) {
      return appPromotionBannerLinks.app_store_link;
    } else if (getOsName() === OSType.ANDROID) {
      return appPromotionBannerLinks.play_store_link;
    }
    return undefined;
  };

  getDeepLinkUrlV2 = (
    location: History.Location | Location,
    queryParams?: QueryParams
  ) => {
    return getDeepLinkUrl(location, queryParams);
  };

  @computed
  private get getDeepLinkUrl() {
    const appPromotionBannerLinks = getSettingAppPromotionBannerLinks();

    if (getOsName() === OSType.IOS) {
      return appPromotionBannerLinks.ios_deep_link;
    } else if (getOsName() === OSType.ANDROID) {
      return appPromotionBannerLinks.android_deep_link;
    }
    return undefined;
  }

  @computed
  private get shouldShowBanner(): boolean {
    return (
      !isMobileApp() &&
      !isEmpty(this.bannerLinkUrl) &&
      !!UserStore.get().userToken
    );
  }
}
export const useMobileAppBannerService = createStoreHook(
  MobileAppBannerService
);

export const getDeepLinkUrl = (
  location: History.Location | Location,
  queryParams?: QueryParams
) => {
  const onSearchPage = !!matchPath(location.pathname, {
    path: "/search",
    exact: true,
  });

  let pathName;
  if (onSearchPage) {
    pathName = `${location.pathname}?q=${queryParams?.getOrDefault("q", "")}`;
  } else {
    pathName = location.pathname;
  }

  return `https://faireretailer.page.link/?link=${`https://www.${getFaireBaseUrl()}`}${pathName}${
    isSafari() ? "&efr=1" : ""
  }&apn=${
    getIsProduction() ? "com.faire.retailer" : "com.faire.retailer.internal"
  }&isi=1413340491&ibi=${`com.indigofair.${getSafeEnvName()}`}`;
};
